/* =============================================================================
   BULLION — theme.js
   Global behaviours. No dependencies. Everything is progressive enhancement.
   ========================================================================== */
(() => {
  'use strict';
  const MVS = window.MVS || { routes: {}, strings: {}, motion: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (reduceMotion) document.documentElement.classList.add('motion-off');

  /* ---------- utilities ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 200) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };

  function formatMoney(cents, format = MVS.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);
    const type = match ? match[1] : 'amount';
    const number = (value, precision = 2, thousands = ',', decimal = '.') => {
      value = (value / 100).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? dollars + decimal + parts[1] : dollars;
    };
    let value = '';
    switch (type) {
      case 'amount': value = number(cents, 2); break;
      case 'amount_no_decimals': value = number(cents, 0); break;
      case 'amount_with_comma_separator': value = number(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = number(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = number(cents, 2, "'", '.'); break;
      default: value = number(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const trapFocus = (container) => {
    const nodes = $$(focusable, container).filter(n => n.offsetParent !== null);
    if (!nodes.length) return () => {};
    const first = nodes[0], last = nodes[nodes.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  const toast = (msg, ms = 2600) => {
    const el = $('#Toast'); if (!el) return;
    el.textContent = msg; el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.classList.remove('is-visible'); setTimeout(() => { el.hidden = true; }, 400); }, ms);
  };

  // Last input type, so focus returned by a drawer only shows its ring for
  // keyboard users. A mouse user closing the cart should not find a gold frame
  // left around the button they clicked.
  let keyboardInput = false;
  document.addEventListener('keydown', (e) => { if (e.key === 'Tab') keyboardInput = true; }, true);
  document.addEventListener('pointerdown', () => { keyboardInput = false; }, true);
  const returnFocus = (el) => {
    if (!el || !el.focus) return;
    if (keyboardInput) el.focus({ preventScroll: true });
    else if (el.isConnected) { el.focus({ preventScroll: true, focusVisible: false }); if (el.matches(':focus-visible')) el.blur(); }
  };

  const lockScroll = (lock) => {
    document.body.classList.toggle('is-locked', lock);
  };

  /* ---------- Drawer (menu, cart, facets) ---------- */
  class Drawer extends HTMLElement {
    connectedCallback() {
      this.panel = $('.drawer__panel', this);
      this.overlay = $('.drawer__overlay', this);
      this.overlay && this.overlay.addEventListener('click', () => this.close());
      $$('[data-drawer-close]', this).forEach(b => b.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      this._untrap = trapFocus(this.panel || this);
      const focusTarget = $('[data-drawer-focus]', this) || $('.drawer__close', this);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 60);
      this.dispatchEvent(new CustomEvent('drawer:open'));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      this._untrap && this._untrap();
      returnFocus(this.trigger);
      this.dispatchEvent(new CustomEvent('drawer:close'));
    }
  }
  customElements.define('mvs-drawer', Drawer);
  // Delegated so a trigger always opens the live drawer: Cart.refresh replaces
  // the cart drawer's markup, and a per-element listener would keep pointing
  // at the detached copy.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    const drawer = trigger && document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    drawer.open(trigger);
  });

  /* ---------- Header ---------- */
  class Header extends HTMLElement {
    connectedCallback() {
      this.header = $('.header', this) || this;
      let last = 0;
      const onScroll = () => {
        const y = window.scrollY;
        if ((y > 24) !== (last > 24)) this.header.classList.toggle('is-scrolled', y > 24);
        last = y;
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });

      // dropdowns: hover intent on desktop, click on touch/keyboard
      $$('.nav__item--has-children', this).forEach(item => {
        const link = $('.nav__link', item);
        let t;
        item.addEventListener('mouseenter', () => { if (!finePointer) return; clearTimeout(t); this.openItem(item); });
        item.addEventListener('mouseleave', () => { if (!finePointer) return; t = setTimeout(() => this.closeItem(item), 120); });
        link.addEventListener('click', (e) => {
          if (link.getAttribute('aria-expanded') === 'true' && finePointer) return; // let navigation through
          e.preventDefault();
          item.classList.contains('is-open') ? this.closeItem(item) : this.openItem(item);
        });
        item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closeItem(item); link.focus(); } });
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) $$('.nav__item.is-open', this).forEach(i => this.closeItem(i)); });

      // mobile menu accordions
      $$('.menu-drawer__link[aria-controls]', this).forEach(btn => {
        btn.addEventListener('click', () => {
          const sub = document.getElementById(btn.getAttribute('aria-controls'));
          const open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!open));
          if (sub) sub.hidden = open;
        });
      });
    }
    openItem(item) { $$('.nav__item.is-open', this).forEach(i => i !== item && this.closeItem(i)); item.classList.add('is-open'); $('.nav__link', item).setAttribute('aria-expanded', 'true'); }
    closeItem(item) { item.classList.remove('is-open'); $('.nav__link', item).setAttribute('aria-expanded', 'false'); }
  }
  customElements.define('mvs-header', Header);

  /* ---------- Announcement rotation ---------- */
  class Announcement extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.interval || '5000', 10);
      $('[data-announce-prev]', this)?.addEventListener('click', () => this.go(-1, true));
      $('[data-announce-next]', this)?.addEventListener('click', () => this.go(1, true));
      if (!reduceMotion) this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => !reduceMotion && this.start());
    }
    go(dir, manual) { this.items[this.index].classList.remove('is-active'); this.index = (this.index + dir + this.items.length) % this.items.length; this.items[this.index].classList.add('is-active'); if (manual) { this.stop(); this.start(); } }
    start() { this.stop(); this.timer = setInterval(() => this.go(1), this.interval); }
    stop() { clearInterval(this.timer); }
  }
  customElements.define('mvs-announcement', Announcement);

  /* ---------- Scroll reveal ---------- */
  if (MVS.motion.reveal !== false && !reduceMotion && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    const observe = (root = document) => $$('[data-reveal], [data-reveal-stagger]', root).forEach(el => io.observe(el));
    observe();
    window.MVS.observeReveal = observe;
  } else {
    document.documentElement.classList.add('motion-off');
  }

  /* ---------- Plinth glow (pointer devices only) ----------
     Bullion does not tilt its cards: on a dark navy page a rotating card reads
     as a glitch, not as depth. Instead the pointer drags a soft champagne light
     across the card face, which is what a struck coin actually does under a
     moving light. The element keeps its geometry; only --glare-x/y change. */
  const glowEnabled = MVS.motion.glow !== false && !reduceMotion && finePointer;
  function bindGlow(el) {
    if (!glowEnabled || el._glow) return;
    el._glow = true;
    let raf = null;
    const move = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty('--glare-x', `${(px * 100).toFixed(1)}%`);
        el.style.setProperty('--glare-y', `${(py * 100).toFixed(1)}%`);
      });
    };
    el.addEventListener('pointerenter', () => el.classList.add('is-lit'));
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', () => { cancelAnimationFrame(raf); el.classList.remove('is-lit'); });
  }
  const bindAllGlow = (root = document) => $$('[data-glow]', root).forEach(bindGlow);
  bindAllGlow();
  window.MVS.bindGlow = bindAllGlow;

  /* ---------- Parallax (hero / editorial media) ---------- */
  if (MVS.motion.parallax !== false && !reduceMotion) {
    const layers = $$('[data-parallax]');
    if (layers.length) {
      let ticking = false;
      const update = () => {
        const vh = window.innerHeight;
        layers.forEach(el => {
          const r = el.parentElement.getBoundingClientRect();
          if (r.bottom < 0 || r.top > vh) return;
          const speed = parseFloat(el.dataset.parallax) || 0.18;
          const offset = (r.top + r.height / 2 - vh / 2) * speed;
          el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
        });
        ticking = false;
      };
      window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
      update();
    }
    // mouse-follow float (hero product)
    $$('[data-mouse-follow]').forEach(zone => {
      if (!finePointer) return;
      const target = $('[data-mouse-follow-target]', zone) || zone;
      const strength = parseFloat(zone.dataset.mouseFollow) || 14;
      zone.addEventListener('pointermove', (e) => {
        const r = zone.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        target.style.setProperty('--mx', `${(px * strength).toFixed(1)}px`);
        target.style.setProperty('--my', `${(py * strength).toFixed(1)}px`);
        target.style.setProperty('--rx', `${(-py * 6).toFixed(2)}deg`);
        target.style.setProperty('--ry', `${(px * 6).toFixed(2)}deg`);
      });
      zone.addEventListener('pointerleave', () => ['--mx', '--my', '--rx', '--ry'].forEach(p => target.style.removeProperty(p)));
    });
  }

  /* ---------- Interactive hero: cursor spotlight, ring tilt, magnetic buttons ---------- */
  $$('[data-hero-live]').forEach(hero => {
    if (!finePointer || reduceMotion) return;
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      hero.style.setProperty('--hx', `${(x * 100).toFixed(1)}%`);
      hero.style.setProperty('--hy', `${(y * 100).toFixed(1)}%`);
      hero.style.setProperty('--px', (x - 0.5).toFixed(3));
      hero.style.setProperty('--py', (y - 0.5).toFixed(3));
      hero.classList.add('is-active');
    });
    hero.addEventListener('pointerleave', () => { hero.classList.remove('is-active'); hero.style.setProperty('--px', 0); hero.style.setProperty('--py', 0); });
    $$('[data-magnetic]', hero).forEach(btn => {
      btn.addEventListener('pointermove', (e) => {
        const r = btn.getBoundingClientRect();
        btn.style.transform = `translate(${((e.clientX - r.left - r.width / 2) * 0.18).toFixed(1)}px, ${((e.clientY - r.top - r.height / 2) * 0.28).toFixed(1)}px)`;
      });
      btn.addEventListener('pointerleave', () => { btn.style.transform = ''; });
    });
  });

  /* ---------- Cart API ---------- */
  const Cart = {
    sectionsFor() { return MVS.cartType === 'drawer' ? 'cart-drawer' : 'main-cart'; },
    async request(url, body) {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || MVS.strings.cartError);
      return data;
    },
    async add(items, opts = {}) {
      const data = await this.request(MVS.routes.cartAdd + '.js', { items, sections: this.sectionsFor(), sections_url: window.location.pathname });
      await this.refresh(data.sections);
      this.bump();
      if (MVS.cartType === 'drawer' && !opts.silent) { const d = $('#CartDrawer'); d && d.open(opts.trigger); }
      else if (!opts.silent) toast(MVS.strings.added);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      return data;
    },
    async change(id, quantity) {
      const data = await this.request(MVS.routes.cartChange + '.js', { id, quantity, sections: this.sectionsFor(), sections_url: window.location.pathname });
      await this.refresh(data.sections, data);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },
    async refresh(sections, cart) {
      if (!cart) { cart = await (await fetch(MVS.routes.cart + '.js')).json(); }
      $$('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; el.dataset.count = cart.item_count; });
      if (sections) {
        Object.entries(sections).forEach(([id, html]) => {
          const target = document.getElementById(`shopify-section-${id}`);
          if (!target || !html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.getElementById(`shopify-section-${id}`) || doc.body.firstElementChild;
          const wasOpen = $('.drawer', target)?.classList.contains('is-open');
          target.innerHTML = fresh.innerHTML;
          if (wasOpen) { const d = $('.drawer', target); if (d) { d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false'); } }
          bindAllGlow(target);
          window.MVS.observeReveal && window.MVS.observeReveal(target);
        });
      }
    },
    bump() {
      $$('[data-cart-count]').forEach(el => { el.classList.remove('is-bumping'); void el.offsetWidth; el.classList.add('is-bumping'); });
      $$('[data-cart-icon]').forEach(el => { el.classList.remove('is-bumping'); void el.offsetWidth; el.classList.add('is-bumping'); });
    }
  };
  window.MVS.Cart = Cart;
  window.MVS.formatMoney = formatMoney;
  window.MVS.toast = toast;

  /* ---------- Product form (AJAX add) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.button = $('[type="submit"]', this.form);
      this.error = $('.product-form__error', this);
      this.form.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      e.preventDefault();
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      const fd = new FormData(this.form);
      const id = parseInt(fd.get('id'), 10);
      const quantity = parseInt(fd.get('quantity') || '1', 10);
      const properties = {};
      fd.forEach((v, k) => { const m = k.match(/^properties\[(.+)\]$/); if (m && v) properties[m[1]] = v; });
      this.button.classList.add('is-loading'); this.button.setAttribute('aria-busy', 'true');
      this.error && this.error.classList.remove('is-visible');
      try {
        await Cart.add([{ id, quantity, properties }], { trigger: this.button });
      } catch (err) {
        if (this.error) { this.error.textContent = err.message; this.error.classList.add('is-visible'); } else toast(err.message);
      } finally {
        this.button.classList.remove('is-loading'); this.button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Quick add (cards) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const id = parseInt(btn.dataset.quickAdd, 10);
    if (!id) { window.location.href = btn.dataset.url; return; }
    btn.classList.add('is-loading');
    try { await Cart.add([{ id, quantity: 1 }], { trigger: btn }); }
    catch (err) { toast(err.message); }
    finally { btn.classList.remove('is-loading'); }
  });

  /* ---------- Quantity input ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const input = $('input', btn.closest('.quantity'));
    const min = parseInt(input.min || '1', 10), max = input.max ? parseInt(input.max, 10) : Infinity;
    let v = parseInt(input.value || '1', 10) + parseInt(btn.dataset.qtyChange, 10);
    v = Math.max(min, Math.min(max, v));
    if (v !== parseInt(input.value, 10)) { input.value = v; input.dispatchEvent(new Event('change', { bubbles: true })); }
  });

  /* ---------- Cart drawer / cart page line updates ---------- */
  const lineChange = debounce(async (key, qty, line) => {
    line && line.classList.add('is-removing');
    try { await Cart.change(key, qty); } catch (err) { toast(err.message); line && line.classList.remove('is-removing'); }
  }, 250);
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (!input) return;
    lineChange(input.dataset.lineQty, parseInt(input.value, 10), input.closest('.cart-item'));
  });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-line-remove]');
    if (!btn) return;
    e.preventDefault();
    lineChange(btn.dataset.lineRemove, 0, btn.closest('.cart-item'));
  });
  document.addEventListener('change', debounce(async (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    try { await fetch(MVS.routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) }); } catch (_) {}
  }, 400));

  /* ---------- Search overlay + predictive search ---------- */
  class SearchOverlay extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      this.defaultHtml = this.results ? this.results.innerHTML : '';
      $$('[data-search-open]').forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); this.open(b); }));
      $$('[data-search-close]', this).forEach(b => b.addEventListener('click', () => this.close()));
      $('.search-overlay__backdrop', this)?.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      document.addEventListener('keydown', (e) => { if (e.key === '/' && !/input|textarea/i.test(document.activeElement.tagName)) { e.preventDefault(); this.open(); } });
      this.input && this.input.addEventListener('input', debounce(() => this.search(), 220));
    }
    open(trigger) { this.trigger = trigger; this.classList.add('is-open'); this.setAttribute('aria-hidden', 'false'); lockScroll(true); this._untrap = trapFocus(this); setTimeout(() => this.input && this.input.focus(), 80); }
    close() { this.classList.remove('is-open'); this.setAttribute('aria-hidden', 'true'); lockScroll(false); this._untrap && this._untrap(); returnFocus(this.trigger); }
    async search() {
      const q = this.input.value.trim();
      if (!this.results) return;
      if (q.length < 2) { this.results.innerHTML = this.defaultHtml; return; }
      this.results.setAttribute('aria-busy', 'true');
      try {
        const url = `${MVS.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=6&resources[limit_scope]=each&section_id=predictive-search`;
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = $('#shopify-section-predictive-search', doc);
        if (this.input.value.trim() === q) this.results.innerHTML = inner ? inner.innerHTML : '';
      } catch (_) {} finally { this.results.removeAttribute('aria-busy'); }
    }
  }
  customElements.define('mvs-search', SearchOverlay);

  /* ---------- Wishlist-ready (localStorage; swap for an app later) ---------- */
  const WISH_KEY = 'mvs_wishlist';
  const getWish = () => { try { return JSON.parse(localStorage.getItem(WISH_KEY) || '[]'); } catch (_) { return []; } };
  const syncWish = (root = document) => { const list = getWish(); $$('[data-wishlist]', root).forEach(b => b.setAttribute('aria-pressed', String(list.includes(b.dataset.wishlist)))); };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist]');
    if (!btn) return;
    e.preventDefault();
    let list = getWish(); const h = btn.dataset.wishlist;
    list = list.includes(h) ? list.filter(x => x !== h) : [...list, h];
    try { localStorage.setItem(WISH_KEY, JSON.stringify(list)); } catch (_) {}
    syncWish();
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: list }));
  });
  syncWish();
  window.MVS.syncWishlist = syncWish;

  /* ---------- Recently viewed tracker ---------- */
  const RV_KEY = 'mvs_recent';
  window.MVS.trackRecent = (handle) => {
    try {
      let list = JSON.parse(localStorage.getItem(RV_KEY) || '[]').filter(h => h !== handle);
      list.unshift(handle); localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (_) {}
  };
  window.MVS.getRecent = () => { try { return JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (_) { return []; } };

  /* ---------- Share ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    const data = { title: document.title, url: window.location.href };
    if (navigator.share) { try { await navigator.share(data); } catch (_) {} }
    else { try { await navigator.clipboard.writeText(data.url); toast(btn.dataset.share || 'Link copied'); } catch (_) {} }
  });

  /* ---------- Localization / misc forms ---------- */
  $$('[data-auto-submit]').forEach(el => el.addEventListener('change', () => el.closest('form').submit()));

  /* ---------- Theme editor hooks ---------- */
  document.addEventListener('shopify:section:load', (e) => { bindAllGlow(e.target); window.MVS.observeReveal && window.MVS.observeReveal(e.target); syncWish(e.target); });
  document.addEventListener('shopify:section:select', (e) => { const d = $('mvs-drawer', e.target); d && d.open(); });
  document.addEventListener('shopify:section:deselect', (e) => { const d = $('mvs-drawer', e.target); d && d.close(); });
})();
